import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAttendanceHm } from '../utils/attendanceTime';
import { AttendanceCheckOutTime } from './AttendanceCheckOutTime';
import { attendanceAPI } from '../services/api';
import { AttendanceBoardTimeAlerts } from './AttendanceBoardTimeAlerts';
import {
  DAY_COMPLETION_LABEL,
  SUPERVISOR_TODAY_STATUS_LABEL,
  dayCompletionBadgeClass,
  resolveDayCompletion,
  supervisorTodayStatusBadgeClass,
} from '../utils/supervisorTodayStatus';
import { addDaysLocalYmd, formatLocalDateYmd } from '../utils/localCalendarDate';

const SupervisorDashboard = ({ onOpenMessagesWithEmployee }) => {
  const [supervisorFactories, setSupervisorFactories] = useState([]);
  const [day, setDay] = useState(() => formatLocalDateYmd());
  const [factoryId, setFactoryId] = useState('');
  const [employeeUserId, setEmployeeUserId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [timeAlertOpenKey, setTimeAlertOpenKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await attendanceAPI.myFactories();
        const all = res.data || [];
        setSupervisorFactories(all);
      } catch {
        setSupervisorFactories([]);
      }
    })();
  }, []);

  const loadRows = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const params = { day };
      if (factoryId) params.factory_id = Number(factoryId);
      if (employeeUserId) params.employee_user_id = Number(employeeUserId);
      const res = await attendanceAPI.supervisorTeamBoard(params);
      setRows(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo cargar el tablero');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [day, factoryId, employeeUserId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setTimeAlertOpenKey(null);
  }, [day]);

  const employeeOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => m.set(r.employee_user_id, r.employee_name));
    return Array.from(m.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  useEffect(() => {
    if (!employeeUserId) return;
    if (!employeeOptions.some((o) => String(o.id) === String(employeeUserId))) {
      setEmployeeUserId('');
    }
  }, [employeeUserId, employeeOptions]);

  const summary = useMemo(() => {
    const status = {
      present: 0,
      late: 0,
      absent: 0,
      early_exit: 0,
      scheduled: 0,
      no_shift: 0,
      other: 0,
    };
    const jornada = { complete: 0, in_progress: 0, incomplete: 0 };
    rows.forEach((r) => {
      if (Object.prototype.hasOwnProperty.call(status, r.status)) status[r.status] += 1;
      else status.other += 1;
      const jc = resolveDayCompletion(r, day);
      if (jc === 'complete') jornada.complete += 1;
      else if (jc === 'in_progress') jornada.in_progress += 1;
      else if (jc === 'incomplete') jornada.incomplete += 1;
    });
    return { status, jornada };
  }, [rows, day]);

  const byFactory = useMemo(() => {
    const groups = {};
    rows.forEach((r) => {
      const k = r.factory_name || `Sede ${r.factory_id}`;
      if (!groups[k]) groups[k] = [];
      groups[k].push(r);
    });
    return groups;
  }, [rows]);

  const statusSummaryKeys = ['present', 'late', 'early_exit', 'absent', 'scheduled', 'no_shift'];
  const jornadaSummaryKeys = ['complete', 'in_progress', 'incomplete'];

  const rowKey = (r) => `${r.factory_id}-${r.employee_user_id}`;
  const toggleEmployeeDetailModal = (r) => {
    setSelectedDetail((prev) => (prev && rowKey(prev) === rowKey(r) ? null : r));
  };

  useEffect(() => {
    setSelectedDetail((prev) => {
      if (!prev) return prev;
      return rows.some((r) => rowKey(r) === rowKey(prev)) ? prev : null;
    });
  }, [rows]);

  const selectedJornada = selectedDetail ? resolveDayCompletion(selectedDetail, day) : null;

  return (
    <div className="space-y-4">
      <div className="card-mobile">
        <h2 className="text-lg font-semibold text-gray-900">Dashboard supervisión</h2>
        <p className="text-sm text-gray-500 mt-1 leading-relaxed">
          Vista del día de las personas que te asignaron como supervisor: turno previsto, ingreso y egreso, y si
          cumplieron horario.
        </p>
      </div>

      <div className="card-mobile space-y-3">
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              className="btn-mobile btn-secondary px-2 py-1 min-h-0 text-sm"
              onClick={() => setDay((prev) => addDaysLocalYmd(prev, -1))}
            >
              ◀
            </button>
            <input type="date" className="input-mobile w-auto min-w-[10.5rem]" value={day} onChange={(e) => setDay(e.target.value)} />
            <button
              type="button"
              className="btn-mobile btn-secondary px-2 py-1 min-h-0 text-sm"
              onClick={() => setDay((prev) => addDaysLocalYmd(prev, 1))}
            >
              ▶
            </button>
          </div>
          <select
            className="input-mobile flex-1 min-w-[12rem]"
            aria-label="Filtrar por sede"
            value={factoryId}
            onChange={(e) => setFactoryId(e.target.value)}
          >
            <option value="">Todas las sedes</option>
            {supervisorFactories.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <select
            className="input-mobile flex-1 min-w-[12rem]"
            aria-label="Filtrar por empleado"
            value={employeeUserId}
            onChange={(e) => setEmployeeUserId(e.target.value)}
            disabled={employeeOptions.length === 0}
          >
            <option value="">Todos los empleados</option>
            {employeeOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 w-full sm:w-auto sm:mr-1">Estado:</span>
            {statusSummaryKeys.map((key) => (
              <span key={key} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                {SUPERVISOR_TODAY_STATUS_LABEL[key]}: <strong>{summary.status[key]}</strong>
              </span>
            ))}
            {summary.status.other > 0 && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                Otros: <strong>{summary.status.other}</strong>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 w-full sm:w-auto sm:mr-1">Jornada:</span>
            {jornadaSummaryKeys.map((key) => (
              <span key={key} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
                {DAY_COMPLETION_LABEL[key]}: <strong>{summary.jornada[key]}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="relative space-y-4 min-h-[14rem] md:min-h-[min(72vh,44rem)]">
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-slate-100/85 backdrop-blur-[1px] border border-slate-200/80 pointer-events-auto"
            aria-busy="true"
            aria-live="polite"
          >
            <p className="text-sm font-medium text-slate-600 bg-white px-4 py-2.5 rounded-lg shadow-sm border border-slate-200/90">
              Cargando…
            </p>
          </div>
        )}

        {!loading && rows.length === 0 && !err && (
          <div className="card-mobile text-sm text-gray-500 leading-relaxed">
            No hay registros para esta fecha y filtros. Si recién configuraste supervisión, pedile al dueño que confirme
            las asignaciones supervisor → empleado en cada sede.
          </div>
        )}

        {Object.entries(byFactory).map(([fname, list]) => (
        <div key={fname} className="card-mobile">
          <h3 className="font-semibold text-gray-900 border-b border-slate-100 pb-2">{fname}</h3>
          <div className="hidden md:block mt-3 overflow-x-auto">
            <table className="w-full text-sm text-left min-w-[720px]">
              <thead>
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="py-3 pr-3 font-medium">Empleado</th>
                  <th className="py-3 pr-3 font-medium">Turno</th>
                  <th className="py-3 pr-3 font-medium">Entrada</th>
                  <th className="py-3 pr-3 font-medium">Salida</th>
                  <th className="py-3 pr-3 font-medium">Jornada</th>
                  <th className="py-3 pr-3 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r) => {
                  const jc = resolveDayCompletion(r, day);
                  return (
                  <tr key={`${r.factory_id}-${r.employee_user_id}`} className="border-b border-gray-100">
                    <td className="py-3 pr-3">
                      <button
                        type="button"
                        className="font-medium text-left text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                        onClick={() => toggleEmployeeDetailModal(r)}
                      >
                        {r.employee_name}
                      </button>
                    </td>
                    <td className="py-3 pr-3 text-gray-600">
                      {r.shift_name ? `${r.shift_name} (${r.shift_start}–${r.shift_end})` : '—'}
                    </td>
                    <td className="py-3 pr-3">
                      <span className="inline-flex flex-wrap items-center gap-1.5 tabular-nums text-gray-800">
                        {formatAttendanceHm(r.check_in_time)}
                        <AttendanceBoardTimeAlerts
                          row={r}
                          boardDateYmd={day}
                          openKey={timeAlertOpenKey}
                          setOpenKey={setTimeAlertOpenKey}
                          rowKey={`sup-${r.factory_id}-${r.employee_user_id}`}
                          showExit={false}
                        />
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      <span className="inline-flex flex-wrap items-center gap-1.5 text-gray-800">
                        <AttendanceCheckOutTime
                          iso={r.check_out_time}
                          checkOutNextDay={r.check_out_next_day}
                        />
                        <AttendanceBoardTimeAlerts
                          row={r}
                          boardDateYmd={day}
                          openKey={timeAlertOpenKey}
                          setOpenKey={setTimeAlertOpenKey}
                          rowKey={`sup-${r.factory_id}-${r.employee_user_id}`}
                          showEntry={false}
                        />
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      {jc === 'none' ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <span className={`status-badge ${dayCompletionBadgeClass(jc)}`}>
                          {DAY_COMPLETION_LABEL[jc] || jc}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <span className={`status-badge ${supervisorTodayStatusBadgeClass(r.status)}`}>
                        {SUPERVISOR_TODAY_STATUS_LABEL[r.status] || r.status}
                      </span>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
          <div className="md:hidden mt-3 space-y-3">
            {list.map((r) => {
              const jcMobile = resolveDayCompletion(r, day);
              return (
              <div
                key={`${r.factory_id}-${r.employee_user_id}`}
                className="rounded-lg border border-slate-200 p-4"
              >
                <div className="flex justify-between gap-2 items-start">
                  <button
                    type="button"
                    className="font-medium text-left text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded min-w-0"
                    onClick={() => toggleEmployeeDetailModal(r)}
                  >
                    {r.employee_name}
                  </button>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {jcMobile !== 'none' && (
                      <span className={`status-badge ${dayCompletionBadgeClass(jcMobile)}`}>
                        {DAY_COMPLETION_LABEL[jcMobile]}
                      </span>
                    )}
                    <span className={`status-badge ${supervisorTodayStatusBadgeClass(r.status)}`}>
                      {SUPERVISOR_TODAY_STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {r.shift_name ? `${r.shift_name} · ${r.shift_start}–${r.shift_end}` : 'Sin turno asignado'}
                </p>
                <div className="text-xs text-gray-500 mt-2 space-y-1.5 tabular-nums leading-relaxed">
                  <p className="inline-flex flex-wrap items-center gap-1.5">
                    <span className="text-gray-600">Entrada</span>
                    {formatAttendanceHm(r.check_in_time)}
                    <AttendanceBoardTimeAlerts
                      row={r}
                      boardDateYmd={day}
                      openKey={timeAlertOpenKey}
                      setOpenKey={setTimeAlertOpenKey}
                      rowKey={`sup-${r.factory_id}-${r.employee_user_id}`}
                      showExit={false}
                    />
                  </p>
                  <p className="inline-flex flex-wrap items-center gap-1.5">
                    <span className="text-gray-600">Salida</span>
                    <AttendanceCheckOutTime
                      iso={r.check_out_time}
                      checkOutNextDay={r.check_out_next_day}
                    />
                    <AttendanceBoardTimeAlerts
                      row={r}
                      boardDateYmd={day}
                      openKey={timeAlertOpenKey}
                      setOpenKey={setTimeAlertOpenKey}
                      rowKey={`sup-${r.factory_id}-${r.employee_user_id}`}
                      showEntry={false}
                    />
                  </p>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      ))}
      </div>

      {selectedDetail && (
        <div className="modal-overlay center" onClick={() => setSelectedDetail(null)}>
          <div className="modal-mobile center max-w-lg w-[calc(100%-2rem)]" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold text-slate-900">Detalle del empleado</h3>
              <button
                type="button"
                onClick={() => setSelectedDetail(null)}
                className="text-2xl leading-none text-slate-500 hover:text-slate-800"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div className="modal-content space-y-4">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Nombre</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">{selectedDetail.employee_name}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Sede</dt>
                  <dd className="mt-0.5 text-gray-800">{selectedDetail.factory_name || '—'}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Turno</dt>
                  <dd className="mt-0.5 text-gray-800">
                    {selectedDetail.shift_name
                      ? `${selectedDetail.shift_name} (${selectedDetail.shift_start}–${selectedDetail.shift_end})`
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Entrada</dt>
                  <dd className="mt-0.5 inline-flex flex-wrap items-center gap-1.5 tabular-nums text-gray-800">
                    {formatAttendanceHm(selectedDetail.check_in_time)}
                    <AttendanceBoardTimeAlerts
                      row={selectedDetail}
                      boardDateYmd={day}
                      openKey={timeAlertOpenKey}
                      setOpenKey={setTimeAlertOpenKey}
                      rowKey={`sup-m-${selectedDetail.factory_id}-${selectedDetail.employee_user_id}`}
                      showExit={false}
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Salida</dt>
                  <dd className="mt-0.5 inline-flex flex-wrap items-center gap-1.5 text-gray-800">
                    <AttendanceCheckOutTime
                      iso={selectedDetail.check_out_time}
                      checkOutNextDay={selectedDetail.check_out_next_day}
                    />
                    <AttendanceBoardTimeAlerts
                      row={selectedDetail}
                      boardDateYmd={day}
                      openKey={timeAlertOpenKey}
                      setOpenKey={setTimeAlertOpenKey}
                      rowKey={`sup-m-${selectedDetail.factory_id}-${selectedDetail.employee_user_id}`}
                      showEntry={false}
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Jornada</dt>
                  <dd className="mt-0.5">
                    {selectedJornada === 'none' ? (
                      <span className="text-gray-400">—</span>
                    ) : (
                      <span className={`status-badge ${dayCompletionBadgeClass(selectedJornada)}`}>
                        {DAY_COMPLETION_LABEL[selectedJornada]}
                      </span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Estado</dt>
                  <dd className="mt-0.5">
                    <span className={`status-badge ${supervisorTodayStatusBadgeClass(selectedDetail.status)}`}>
                      {SUPERVISOR_TODAY_STATUS_LABEL[selectedDetail.status] || selectedDetail.status}
                    </span>
                  </dd>
                </div>
              </dl>
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap pt-1">
                {typeof onOpenMessagesWithEmployee === 'function' && (
                  <button
                    type="button"
                    className="btn-mobile btn-primary text-sm py-2.5 min-h-[44px] sm:flex-1"
                    onClick={() => {
                      setSelectedDetail(null);
                      onOpenMessagesWithEmployee(selectedDetail.employee_user_id, selectedDetail.factory_id);
                    }}
                  >
                    Mensajes
                  </button>
                )}
                <button
                  type="button"
                  className="btn-mobile btn-secondary text-sm py-2.5 min-h-[44px] sm:flex-1"
                  onClick={() => setSelectedDetail(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorDashboard;
